/**
 * The selector registry behind the single delegated listener.
 *
 * Every menu definition is a CSS selector plus what to show. One `contextmenu`
 * listener on the delegation root asks this module, once per event, which
 * definition owns the clicked element. Nothing here touches the menu itself: it
 * answers a question about the DOM and returns a definition, so it can be
 * tested with plain elements and no menu at all.
 */

import type { MenuDefinition } from './types'

/** A definition and the element it matched, which is the element the menu opens on. */
export interface RegistryMatch {
  def: MenuDefinition
  el: HTMLElement
}

/** A registration, tagged with the order it arrived in so ties are decidable. */
interface Entry {
  def: MenuDefinition
  order: number
}

/** `priority` is optional in the contract; an absent one means zero. */
function priorityOf(def: MenuDefinition): number {
  return def.priority ?? 0
}

/**
 * `Element.matches` with an invalid selector treated as "no match".
 *
 * A typo in one definition's selector throws a `SyntaxError` that would
 * otherwise abort the whole match walk and take every other menu on the page
 * down with it.
 */
function matchesSelector(el: Element, selector: string): boolean {
  try {
    return el.matches(selector)
  } catch {
    console.error(`[context-menu] ignoring a definition with an invalid selector: ${selector}`)

    return false
  }
}

export class Registry {
  readonly #entries: Entry[] = []
  #nextOrder = 0

  /**
   * Register a definition. The returned disposer removes exactly this
   * registration, never a later one that happens to be the same object, and is
   * safe to call more than once.
   */
  add(def: MenuDefinition): () => void {
    const entry: Entry = { def, order: this.#nextOrder }

    this.#nextOrder += 1
    this.#entries.push(entry)

    let disposed = false

    return () => {
      if (disposed) return

      disposed = true

      const index = this.#entries.indexOf(entry)

      if (index !== -1) this.#entries.splice(index, 1)
    }
  }

  /**
   * Find the definition that owns `target`.
   *
   * The walk runs from the target outwards and stops at the first element any
   * definition matches, so the deepest match wins: a `.cell` inside a `.row`,
   * both registered, opens the cell's menu. Among the definitions matching that
   * one element the highest `priority` wins, and a priority tie is broken by
   * registration order.
   *
   * Returns null when nothing matches, when the target is not an element, and
   * when the target is not inside `root`, which also covers a detached element.
   */
  match(target: EventTarget | null, root: HTMLElement | Document): RegistryMatch | null {
    if (this.#entries.length === 0) return null
    if (!(target instanceof Element)) return null
    if (!root.contains(target)) return null

    for (let node: Element | null = target; node !== null; node = node.parentElement) {
      if (node instanceof HTMLElement) {
        const def = this.#bestFor(node)

        if (def !== null) return { def, el: node }
      }

      if (node === root) break
    }

    return null
  }

  /** Drop every registration. The registry stays usable. */
  clear(): void {
    this.#entries.length = 0
  }

  /** The winning definition for one element, or null when none matches it. */
  #bestFor(el: HTMLElement): MenuDefinition | null {
    let best: Entry | null = null

    for (const entry of this.#entries) {
      if (!matchesSelector(el, entry.def.on)) continue

      // Strictly greater, so the earlier registration survives a priority tie.
      if (best === null || priorityOf(entry.def) > priorityOf(best.def)) best = entry
    }

    return best === null ? null : best.def
  }
}
